import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { useNavigate } from "react-router";

import { Button, Collapse, CollapseGroup, Color, Drawer, Icon, Panel, createButtonType } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { Card, ContentContainer, ThemedTitle } from "@/premium/themedComponents";
import { ThemedH3 } from "@/premium/themedComponents/Headers";
import { ThemedButton } from "@/premium/themedComponents/ThemedButton";
import { EnrollmentContext } from "@/state";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";
import { showUnderstandPrescriptionCostsSection } from "@/utils/featureFlags";

import "./Home.scss";

export const Home: FC = () => {
  const { t: translate, i18n } = useTranslation();
  const navigate = useNavigate();
  const urlLocation = window.location;
  const searchParams = new URLSearchParams(urlLocation.search);
  const clientUrlId = searchParams.get("client");
  const [state, _] = useContext(EnrollmentContext);
  const { isLoading } = state;
  const theme = state.experienceData.theme;
  const faqs = state.experienceData.faqs;
  const [expandedFaqs, setExpandedFaqs] = useState<ReadonlyArray<string>>([]);
  const [enrollmentJourneyDrawerStepIndex, setEnrollmentJourneyDrawerStepIndex] = useState<number | null>(null);
  const [stepCaptionIsTruncated, setStepCaptionIsTruncated] = useState<boolean[]>([]);
  const stepCaptionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const headingStyle = { fontFamily: (theme as { font?: string } | undefined)?.font || "Source Sans 3" };
  const titleStyle = { ...headingStyle, fontSize: "28px", fontWeight: 600 };
  const displayMode = useResponsiveDisplayMode();
  const buttonType = createButtonType("enrollment-home-button", Color.DarkGrey[400], Color.DarkGrey[400]);
  const containerClassName = classNames("page-layout-container", { "page-layout-container--mobile": displayMode === "mobile" });

  const {
    headerColumns,
    enrollmentJourneyColumns,
    verticalHeader,
    tabletEnrollmentJourney,
    understandPrescriptionCostsColumns,
    buttonWidthStyle,
  } = useResponsive(
    useMemo(
      () => ({
        headerColumns: { desktop: 6, tablet: 6, mobile: 12 },
        enrollmentJourneyColumns: { desktop: 4, tablet: 12, mobile: 12 },
        verticalHeader: { desktop: false, tablet: false, mobile: true },
        tabletEnrollmentJourney: { desktop: false, tablet: true, mobile: false },
        understandPrescriptionCostsColumns: { desktop: 3, tablet: 6, mobile: 12 },
        buttonWidthStyle: { desktop: {}, tablet: {}, mobile: { width: "100%" } },
      }),
      [],
    ),
  );

  const enrollmentJourneySteps = useMemo(
    () => [
      {
        image: theme.step_01_image,
        imageAlt: theme.step_01_image_alt_text,
        stepLabel: translate("ENROLLMENT_HOME.home.stepOne"),
        caption: theme.step_01_image_caption || "",
      },
      {
        image: theme.step_02_image,
        imageAlt: theme.step_02_image_alt_text,
        stepLabel: translate("ENROLLMENT_HOME.home.stepTwo"),
        caption: theme.step_02_image_caption || "",
      },
      {
        image: theme.step_03_image,
        imageAlt: theme.step_03_image_alt_text,
        stepLabel: translate("ENROLLMENT_HOME.home.stepThree"),
        caption: theme.step_03_image_caption || "",
      },
    ],
    [theme, translate, i18n.language],
  );

  useEffect(() => {
    const measureTruncation = () => {
      const next = enrollmentJourneySteps.map((_, idx) => {
        const element = stepCaptionRefs.current[idx];

        /* 57px is literally the hardestcoded value ive ever made,
           but it accounts for the height of 3 lines of text with the current styling.
           If the scrollHeight is greater than 55, we know the text is truncated
           all of my dignity as a software engineer is lost at this point */

        /* the fact that we wanted to detect truncation to
           conditionally render a button
           that opens a drawer with the full text is wild,
           but unfortunately the designs require us to do this and
           there isn't a great way to detect truncation in CSS */

        /* disclaimer: i did try at one point to make it extensible
          along mobile/tablet/desktop breakpoints, but the fact that the
          width of each character is not consistent and the screen size can vary
          makes it impossible to come up with a javascript solution rather than
          css that truncates at exactly 3 lines of text in all cases
          so here we are */

        return !!element && element.scrollHeight > 57;
      });

      setStepCaptionIsTruncated(next);
    };

    const observer = new ResizeObserver(measureTruncation);

    stepCaptionRefs.current.forEach((element) => {
      if (element) {
        observer.observe(element);
      }
    });

    const animationFrameId = window.requestAnimationFrame(measureTruncation);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [displayMode, enrollmentJourneySteps, tabletEnrollmentJourney]);

  const Header = useMemo(() => {
    const headerClassName = classNames("enrollment-home-section enrollment-home-themed-banner", {
      "enrollment-home-themed-banner--vertical": verticalHeader,
      "enrollment-home-themed-banner--tablet": displayMode === "tablet",
    });
    const headerGridClassName = classNames("enrollment-home-banner-grid", {
      "enrollment-home-banner-grid--vertical": verticalHeader,
      "enrollment-home-banner-grid--horizontal": !verticalHeader,
    });
    const viewPlans = () => {
      navigate(`/plans?client=${clientUrlId}`);
    };

    return (
      <div className={headerClassName}>
        <Grid className={headerGridClassName} spacing={verticalHeader ? "large" : "none"}>
          <Grid.Span columns={headerColumns}>
            <Stack alignment="left" spacing="large" className="enrollment-home-gradient">
              <Stack spacing="small">
                {theme.header_primary_text && <ThemedTitle style={titleStyle}>{theme.header_primary_text}</ThemedTitle>}
                {theme.header_secondary_text && (
                  <Paragraph className="enrollment-home-banner-paragraph">{theme.header_secondary_text}</Paragraph>
                )}
              </Stack>
              <Button className="home-header-button" appearance={buttonType.Primary.Large} onClick={viewPlans}>
                {translate("ENROLLMENT_HOME.home.viewPlans")}
              </Button>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={headerColumns}>
            <div className="enrollment-home-image-container">
              <img className="enrollment-home-header-image" src={theme.home_image} alt={theme.home_image_alt_text} />
            </div>
          </Grid.Span>
        </Grid>
      </div>
    );
  }, [verticalHeader, headerColumns, theme, displayMode, buttonType, translate, i18n.language, navigate, clientUrlId]);

  const EnrollmentJourney = useMemo(() => {
    const stepButtonType = createButtonType("enrollment-home-step-button", Color.DarkGrey[400], Color.DarkGrey[400]);
    const drawerVisible = enrollmentJourneyDrawerStepIndex !== null;
    const drawerStep = enrollmentJourneyDrawerStepIndex !== null ? enrollmentJourneySteps[enrollmentJourneyDrawerStepIndex] : null;

    const drawerFooter = (
      <Shelf>
        <Shelf.Spacer />
        <ThemedButton onClick={() => setEnrollmentJourneyDrawerStepIndex(null)}>{translate("ENROLLMENT_HOME.home.close")}</ThemedButton>
      </Shelf>
    );

    return (
      <div className="enrollment-home-section">
        <Stack alignment="center" spacing="medium" className="enrollment-home-section-header">
          <h2 className="enrollment-home-h2" style={headingStyle}>
            {translate("ENROLLMENT_HOME.home.yourEnrollmentJourney")}
          </h2>
          <Paragraph textAlign="center">{translate("ENROLLMENT_HOME.home.enrollmentJourneyDescription")}</Paragraph>
        </Stack>
        <Grid>
          {enrollmentJourneySteps.map((step, idx) => (
            <Grid.Span columns={enrollmentJourneyColumns} key={idx}>
              <Card fullHeight={true} style={{ borderRadius: "8px" }}>
                {!tabletEnrollmentJourney ? (
                  <Stack className="home-step-container" alignment="left" spacing="medium">
                    <div className="home-step-img-container">
                      <img src={step.image} alt={step.imageAlt} className="home-enrollment-journey-img" />
                    </div>
                    <Stack className="home-step-text-container" alignment="left" spacing="small">
                      <ThemedH3>{step.stepLabel}</ThemedH3>
                      <div
                        ref={(element) => {
                          stepCaptionRefs.current[idx] = element;
                        }}
                        className="home-step-caption"
                      >
                        <Markdown>{step.caption}</Markdown>
                      </div>
                    </Stack>
                    <Stack.Spacer />
                    {!!stepCaptionIsTruncated[idx] && (
                      <Button
                        appearance={stepButtonType.Tertiary.Large}
                        style={buttonWidthStyle}
                        onClick={() => setEnrollmentJourneyDrawerStepIndex(idx)}
                      >
                        {translate("ENROLLMENT_HOME.home.viewMore")}
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Shelf className="home-step-container home-step-container--tablet" alignment="center" spacing="large">
                    <div className="home-step-img-container">
                      <img src={step.image} alt={step.imageAlt} className="home-enrollment-journey-img" />
                    </div>
                    <Stack className="home-step-text-container" alignment="left" spacing="small">
                      <ThemedH3>{step.stepLabel}</ThemedH3>
                      <div
                        ref={(element) => {
                          stepCaptionRefs.current[idx] = element;
                        }}
                        className="home-step-caption"
                      >
                        <Markdown>{step.caption}</Markdown>
                      </div>
                      <Stack.Spacer />
                      {!!stepCaptionIsTruncated[idx] && (
                        <Button
                          appearance={stepButtonType.Tertiary.Large}
                          style={buttonWidthStyle}
                          onClick={() => setEnrollmentJourneyDrawerStepIndex(idx)}
                        >
                          {translate("ENROLLMENT_HOME.home.viewMore")}
                        </Button>
                      )}
                    </Stack>
                  </Shelf>
                )}
              </Card>
            </Grid.Span>
          ))}
        </Grid>
        <Drawer
          id="enrollment-journey-drawer"
          visible={drawerVisible}
          title={drawerStep?.stepLabel}
          footer={drawerFooter}
          onClose={() => setEnrollmentJourneyDrawerStepIndex(null)}
        >
          {drawerStep && (
            <Stack alignment="left" spacing="medium">
              <Paragraph>
                <Markdown>{drawerStep.caption}</Markdown>
              </Paragraph>
            </Stack>
          )}
        </Drawer>
      </div>
    );
  }, [
    buttonWidthStyle,
    enrollmentJourneyColumns,
    enrollmentJourneyDrawerStepIndex,
    enrollmentJourneySteps,
    stepCaptionIsTruncated,
    tabletEnrollmentJourney,
    translate,
  ]);

  const UnderstandPrescriptionCosts = useMemo(() => {
    const tiers = [
      {
        title: translate("ENROLLMENT_HOME.home.tierOne"),
        description: translate("ENROLLMENT_HOME.home.tierOneDescription"),
      },
      {
        title: translate("ENROLLMENT_HOME.home.tierTwo"),
        description: translate("ENROLLMENT_HOME.home.tierTwoDescription"),
      },
      {
        title: translate("ENROLLMENT_HOME.home.tierThree"),
        description: translate("ENROLLMENT_HOME.home.tierThreeDescription"),
      },
      {
        title: translate("ENROLLMENT_HOME.home.tierFour"),
        description: translate("ENROLLMENT_HOME.home.tierFourDescription"),
      },
    ];

    return (
      <div className="enrollment-home-section">
        <Stack alignment="center" spacing="medium" className="enrollment-home-section-header">
          <h2 className="enrollment-home-h2" style={headingStyle}>
            {translate("ENROLLMENT_HOME.home.understandPrescriptionCosts")}
          </h2>
          <Paragraph textAlign="center">{translate("ENROLLMENT_HOME.home.understandPrescriptionCostsDescription")}</Paragraph>
        </Stack>
        <Panel style={{ padding: "0", borderRadius: "10px" }}>
          <Grid>
            {tiers.map((tier, idx) => (
              <Grid.Span columns={understandPrescriptionCostsColumns} key={idx}>
                <Stack alignment="left" spacing="medium" style={{ padding: "24px" }}>
                  <Shelf alignment="center" spacing="mini">
                    {Array.from({ length: idx + 1 }).map((_, iconIdx) => (
                      <Icon name="dollar-circle" key={iconIdx} />
                    ))}
                  </Shelf>
                  <Stack alignment="left" spacing="small">
                    <ThemedH3>{tier.title}</ThemedH3>
                    <Paragraph>{tier.description}</Paragraph>
                  </Stack>
                </Stack>
              </Grid.Span>
            ))}
          </Grid>
        </Panel>
      </div>
    );
  }, [translate, i18n.language, theme]);

  const Faqs = useMemo(() => {
    if (isLoading) {
      return <div className="enrollment-home-section">{translate("ENROLLMENT_HOME.home.loadingFaqs")}</div>;
    } else {
      return (
        faqs.length > 0 && (
          <div className="enrollment-home-section">
            <Stack alignment="center" spacing="medium" className="enrollment-home-section-header">
              <h2 className="enrollment-home-h2" style={headingStyle}>
                {translate("ENROLLMENT_HOME.home.frequentlyAskedQuestions")}
              </h2>
              <Paragraph textAlign="center">{translate("ENROLLMENT_HOME.home.frequentlyAskedQuestionsDescription")}</Paragraph>
            </Stack>
            <CollapseGroup
              alignCaret="left"
              spacing="medium"
              collapseComponent={Collapse.Contained}
              expandedIds={expandedFaqs}
              onExpandedIdsChange={setExpandedFaqs}
            >
              {faqs.map((faq, idx) => {
                return (
                  <CollapseGroup.Collapse
                    id={`faq-cg-${idx}`}
                    className="home-faq-item"
                    key={idx}
                    title={<div className="enrollment-home-question">{faq.question}</div>}
                  >
                    <div className="enrollment-home-answer">
                      <Markdown>{faq.content}</Markdown>
                    </div>
                  </CollapseGroup.Collapse>
                );
              })}
            </CollapseGroup>
          </div>
        )
      );
    }
  }, [faqs, expandedFaqs, isLoading, translate, i18n.language]);

  useEffect(() => {
    logEvent(ANALYTIC_EVENTS.LANDING_PAGE);
  }, []);

  return (
    <ContentContainer noBackground={true}>
      {Header}
      <div className={containerClassName}>
        {theme.show_enrollment_journey && EnrollmentJourney}
        {showUnderstandPrescriptionCostsSection(clientUrlId) && UnderstandPrescriptionCosts}
        {Faqs}
      </div>
    </ContentContainer>
  );
};
